import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getFirestore, collection, getDocs, query, where } from 'firebase/firestore';
import '../styles/HeadToHead.css';
import LastGamesViewer from '../components/LastGamesViewer';
import { fetchActiveSeasonDoc, listSeasons } from '../utils/seasonApi';
import { getSeasonSelection, subscribeSeasonSelection } from '../utils/seasonSelection';
import {
  MATCH_TYPE_CATEGORIES,
  getMatchTypeOptions,
  getMatchTypeRegions,
  getSeasonConfig,
  inferMatchTypeCategory,
} from '../utils/matchTypeCatalog';

const MODES = ['HP', 'SND', 'CTL'];

function normalizeText(value) {
  return String(value || '').trim().toUpperCase();
}

function normalizeMode(value) {
  const raw = normalizeText(value);
  if (raw === 'HARDPOINT') return 'HP';
  if (raw === 'SEARCH' || raw === 'SEARCH & DESTROY' || raw === 'SEARCH AND DESTROY') return 'SND';
  if (raw === 'CONTROL') return 'CTL';
  return raw;
}

function normalizeResult(value) {
  const raw = normalizeText(value);
  if (raw === 'WIN' || raw === 'W') return 'WIN';
  if (raw === 'LOSS' || raw === 'LOSE' || raw === 'L') return 'LOSS';
  return '';
}

function getRowTeamValues(row = {}) {
  return [
    row.Team,
    row.TeamName,
    row.OwnerTeam,
    row.EnemyTeam,
    row.Opponent,
    row.VS,
  ];
}

function rowMatchesTeam(row, teamKeyInput) {
  const teamKeys = normalizeTeamKeys(teamKeyInput);
  if (!teamKeys.length) return true;
  const rowKeys = normalizeTeamKeys(getRowTeamValues(row));
  return rowKeys.some((key) => teamKeys.includes(key));
}

function rowMatchesSharedFilters(row, filters = {}) {
  const { startDate, endDate, matchTypes = [], matchCategory = '', matchTypeRegion = [], matchTypeCatalog = [] } = filters;
  const rowDate = toDateKey(row?.Date || row?.date);
  if (startDate && rowDate && rowDate < startDate) return false;
  if (endDate && rowDate && rowDate > endDate) return false;

  const rowMatchType = normalizeText(row?.MatchType || row?.matchType || row?.Type || row?.type);
  const catalogMatches = findCatalogMatchesForType(matchTypeCatalog, rowMatchType);
  const categoryKey = normalizeText(matchCategory);
  if (categoryKey) {
    const rowCategory = normalizeText(row?.MatchCategory || row?.matchCategory);
    const categoryMatches = rowCategory
      ? rowCategory === categoryKey
      : catalogMatches.length
        ? catalogMatches.some((entry) => normalizeText(entry?.category) === categoryKey)
        : inferMatchTypeCategory(rowMatchType) === categoryKey;
    if (!categoryMatches) return false;
  }

  const selectedTypeRegions = normalizeTeamKeys(matchTypeRegion);
  if (selectedTypeRegions.length) {
    const rowTypeRegion = normalizeText(row?.MatchTypeRegion || row?.matchTypeRegion);
    const catalogRegions = catalogMatches
      .filter((entry) => !categoryKey || normalizeText(entry?.category) === categoryKey)
      .map((entry) => normalizeText(entry?.region || 'GLOBAL'))
      .filter(Boolean);
    const rowTypeRegions = rowTypeRegion
      ? [rowTypeRegion]
      : catalogRegions.length
        ? catalogRegions
        : ['GLOBAL'];
    if (!rowTypeRegions.some((region) => selectedTypeRegions.includes(region))) return false;
  }

  const selectedMatchTypes = normalizeTeamKeys(matchTypes);
  if (selectedMatchTypes.length) {
    if (!selectedMatchTypes.includes(rowMatchType)) return false;
  }

  return true;
}

function filterRowsForView(rows, { teamKeys, region, sharedFilters, allowUnfilteredFallback = false } = {}) {
  const regionKey = normalizeText(region);
  const filtered = (rows || []).filter((row) => {
    if (!rowMatchesTeam(row, teamKeys)) return false;
    if (regionKey) {
      const rowRegion = normalizeText(row?.Region || row?.region);
      if (rowRegion && rowRegion !== regionKey) return false;
    }
    return rowMatchesSharedFilters(row, sharedFilters);
  });

  if (filtered.length || !allowUnfilteredFallback) return filtered;
  return (rows || []).filter((row) => rowMatchesSharedFilters(row, sharedFilters));
}

function buildRowKey(row) {
  if (!row || typeof row !== 'object') return '';
  return String(
    row.ModeID ||
    row.matchGroupId ||
    row.mirrorId ||
    `${row.Team || ''}|${row.TeamName || ''}|${row.EnemyTeam || ''}|${row.Opponent || ''}|${row.VS || ''}|${row.Mode || ''}|${row.Map || ''}|${row.Date || ''}|${row.PF || ''}|${row.PA || ''}`
  );
}

function mergeUniqueRows(...groups) {
  const seen = new Set();
  const out = [];
  groups.flat().forEach((row) => {
    const key = buildRowKey(row);
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateKey(value) {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = toDateValue(value);
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function normalizeTeamKeys(value) {
  const list = Array.isArray(value) ? value : [value];
  return [...new Set(
    list
      .map((item) => normalizeText(item))
      .filter(Boolean)
  )];
}

function findCatalogMatchesForType(catalog = [], matchType = '') {
  const typeKey = normalizeText(matchType);
  if (!typeKey) return [];
  return (catalog || []).filter((item) => normalizeText(item?.label || item?.name || item?.MatchType) === typeKey);
}

function getMatchTypeOptionsForRegions(catalog = [], { category = '', regions = [] } = {}) {
  const selectedRegions = normalizeTeamKeys(regions);
  if (!selectedRegions.length) {
    return getMatchTypeOptions(catalog, { category, region: '' });
  }

  const seen = new Set();
  return selectedRegions
    .flatMap((region) => getMatchTypeOptions(catalog, { category, region }))
    .filter((item) => {
      const key = normalizeText(item?.label);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

function buildModeSummary(rows) {
  const out = MODES.map(m => ({ Mode: m, Wins: 0, Losses: 0, 'Win %': '0%' }));
  const idx = Object.fromEntries(MODES.map((m,i)=>[m,i]));
  rows.forEach(r => {
    const mode = normalizeMode(r.Mode);
    if (!(mode in idx)) return;
    const i = idx[mode];
    const result = normalizeResult(r.WinLose || r.Result);
    if (result === 'WIN') out[i].Wins += 1;
    if (result === 'LOSS') out[i].Losses += 1;
  });
  out.forEach(o => {
    const total = o.Wins + o.Losses;
    o['Win %'] = total ? `${Math.round((o.Wins/total)*100)}%` : '0%';
  });
  return out;
}
function buildMapStats(rows) {
  const byKey = new Map();

  rows.forEach(r => {
    const Mode = normalizeMode(r.Mode);
    const Map = r.Map || '';
    if (!Mode || !Map) return;

    const k = `${Mode}::${Map}`;
    if (!byKey.has(k)) byKey.set(k, {
      Mode, Map, Wins: 0, Losses: 0, PF_sum: 0, PA_sum: 0, GP: 0
    });

    const agg = byKey.get(k);
    const result = normalizeResult(r.WinLose || r.Result);
    if (result === 'WIN') agg.Wins += 1;
    if (result === 'LOSS') agg.Losses += 1;
    agg.PF_sum += Number(r.PF) || 0;
    agg.PA_sum += Number(r.PA) || 0;
    agg.GP += 1;
  });

  return Array.from(byKey.values()).map(r => {
    const PF = r.GP ? r.PF_sum / r.GP : 0;
    const PA = r.GP ? r.PA_sum / r.GP : 0;
    return {
      Mode: r.Mode,
      Map: r.Map,
      Wins: r.Wins,
      Losses: r.Losses,
      'Win %': (r.Wins + r.Losses) ? `${Math.round((r.Wins/(r.Wins+r.Losses))*100)}%` : '0%',
      PF: Number(PF.toFixed(1)),
      PA: Number(PA.toFixed(1)),
      '+/-': Number((PF - PA).toFixed(1)),
    };
  });
}

function SummaryCards({ summary }) {
  const totals = summary.reduce((a, s) => ({
    wins: a.wins + (s.Wins || 0),
    losses: a.losses + (s.Losses || 0),
  }), { wins: 0, losses: 0 });
  const wr = (totals.wins + totals.losses)
    ? `${Math.round((totals.wins / (totals.wins + totals.losses)) * 100)}%`
    : '0%';

  return (
    <div className="summary-cards">
      <div className="card total">
        <h4>All Modes</h4>
        <p className="summary-record">{totals.wins} - {totals.losses}</p>
        <p className="summary-win-rate">{wr}</p>
      </div>
      {MODES.map(mode => {
        const row = summary.find(s => s.Mode === mode) || { Wins: 0, Losses: 0, 'Win %': '0%' };
        return (
          <div key={mode} className="card">
            <h4>{mode}</h4>
            <p className="summary-record">{row.Wins} - {row.Losses}</p>
            <p className="summary-win-rate">{row['Win %']}</p>
          </div>
        );
      })}
    </div>
  );
}function ModeTables({ data }) {
  const classForWinPct = (val) => {
    const p = typeof val === 'string' ? parseFloat(val.replace('%','')) : (val || 0);
    if (p >= 60) return 'win-high';
    if (p >= 45) return 'win-mid';
    if (p >= 30) return 'win-low';
    return 'win-bad';
  };
  const classForPlusMinus = (val) => {
    const n = Number(val) || 0;
    if (n >= 10) return 'win-high';
    if (n >= 0)  return 'win-mid';
    if (n > -25) return 'win-low';
    return 'win-bad';
  };

  return MODES.map((mode) => {
    const rows = (data || []).filter(r => r.Mode === mode);
    if (!rows.length) return null;

    return (
      <div key={mode} className="map-performance-container">
        <div className="table-title">Map Performance – {mode}</div>

        <div className="table-wrapper">
          <table className="performance-table performance-table--maps">
            {/* FIXED COLUMN LAYOUT */}
            <colgroup>
              <col className="col-map" />
              <col className="col-w" />
              <col className="col-l" />
              <col className="col-winpct" />
              <col className="col-pf" />
              <col className="col-pa" />
              <col className="col-diff" />
            </colgroup>

            <thead>
              <tr>
                <th>Map</th>
                <th>W</th>
                <th>L</th>
                <th>Win %</th>
                <th>PF</th>
                <th>PA</th>
                <th>[+/-]</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .sort((a, b) => (b['+/-'] ?? 0) - (a['+/-'] ?? 0))
                .map((r, i) => (
                  <tr key={`${mode}-${r.Map}-${i}`}>
                    <td>{r.Map}</td>
                    <td>{r.Wins}</td>
                    <td>{r.Losses}</td>
                    <td className={`win-rate ${classForWinPct(r['Win %'])}`}>{r['Win %']}</td>
                    <td>{r.PF}</td>
                    <td>{r.PA}</td>
                    <td className={`win-rate ${classForPlusMinus(r['+/-'])}`}>{r['+/-']}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  });
}
export default function HeadToHead() {
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  // --- states first ---
  const [user, setUser] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [config, setConfig] = useState(null);
  const [ownerTeamName, setOwnerTeamName] = useState('');
  const [ownerTeamLabel, setOwnerTeamLabel] = useState('');
  const [baseOwnerTeamLabel, setBaseOwnerTeamLabel] = useState('');

  const [regionA, setRegionA] = useState('');
  const [regionB, setRegionB] = useState('');
  const [teamA, setTeamA] = useState('');
  const [teamB, setTeamB] = useState('');

  const [summaryA, setSummaryA] = useState([]);
  const [summaryB, setSummaryB] = useState([]);
  const [mapDataA, setMapDataA] = useState([]);
  const [mapDataB, setMapDataB] = useState([]);
  const [seasonYearA, setSeasonYearA] = useState(String(new Date().getFullYear()));
  const [seasonConfigYearA, setSeasonConfigYearA] = useState(String(new Date().getFullYear()));
  const [seasonYearB, setSeasonYearB] = useState(String(new Date().getFullYear()));
  const [seasonConfigYearB, setSeasonConfigYearB] = useState(String(new Date().getFullYear()));
  const [availableSeasons, setAvailableSeasons] = useState([]);
  const [startDateA, setStartDateA] = useState('');
  const [endDateA, setEndDateA] = useState('');
  const [matchCategoryA, setMatchCategoryA] = useState('');
  const [matchTypeRegionA, setMatchTypeRegionA] = useState([]);
  const [selectedMatchTypesA, setSelectedMatchTypesA] = useState([]);
  const [startDateB, setStartDateB] = useState('');
  const [endDateB, setEndDateB] = useState('');
  const [matchCategoryB, setMatchCategoryB] = useState('');
  const [matchTypeRegionB, setMatchTypeRegionB] = useState([]);
  const [selectedMatchTypesB, setSelectedMatchTypesB] = useState([]);
  const [matchTypeDropdownOpen, setMatchTypeDropdownOpen] = useState(null);

  // --- now helpers/derived values ---
  const isMyTeam = (val) => {
    const v = (val || '').trim().toUpperCase();
    const fallback = (config?.name || '').trim().toUpperCase();
    return v === '__MY_TEAM__'
      || v === (ownerTeamName || '').toUpperCase()
      || (!!fallback && v === fallback);
  };

  const myTeamA = isMyTeam(teamA);
  const myTeamB = isMyTeam(teamB);

  const sharedFiltersA = useMemo(() => {
    const seasonConfig = getSeasonConfig(config, seasonConfigYearA);
    return {
      startDate: startDateA,
      endDate: endDateA,
      matchCategory: matchCategoryA,
      matchTypeRegion: matchTypeRegionA,
      matchTypes: selectedMatchTypesA,
      matchTypeCatalog: seasonConfig.matchTypeCatalog || [],
    };
  }, [config, seasonConfigYearA, startDateA, endDateA, matchCategoryA, matchTypeRegionA, selectedMatchTypesA]);

  const sharedFiltersB = useMemo(() => {
    const seasonConfig = getSeasonConfig(config, seasonConfigYearB);
    return {
      startDate: startDateB,
      endDate: endDateB,
      matchCategory: matchCategoryB,
      matchTypeRegion: matchTypeRegionB,
      matchTypes: selectedMatchTypesB,
      matchTypeCatalog: seasonConfig.matchTypeCatalog || [],
    };
  }, [config, seasonConfigYearB, startDateB, endDateB, matchCategoryB, matchTypeRegionB, selectedMatchTypesB]);

  const resolveSeasonContext = useCallback(async (requestedSeasonId = null) => {
    if (!teamId) return null;

    let seasons = [];
    try {
      seasons = await listSeasons(teamId);
    } catch (err) {
      console.error('Failed to list seasons for Head-to-head', err);
    }

    const normalized = seasons.map((s) => ({
      id: String(s.id),
      year: s?.year != null ? String(s.year) : String(s.id),
      status: (s?.status || '').toLowerCase(),
      startDate: s?.startDate || null,
      endDate: s?.endDate || null,
    }));
    setAvailableSeasons(normalized);

    const findSeason = (value) => {
      if (!value) return null;
      const key = String(value);
      return normalized.find((s) => s.id === key || s.year === key) || null;
    };

    const explicitMatch = findSeason(requestedSeasonId);
    if (explicitMatch) {
      return {
        seasonId: explicitMatch.id,
        seasonConfigKey: explicitMatch.year,
        startDate: explicitMatch.startDate || '',
        endDate: explicitMatch.endDate || '',
      };
    }
    if (requestedSeasonId) {
      const fallbackKey = String(requestedSeasonId);
      return { seasonId: fallbackKey, seasonConfigKey: fallbackKey, startDate: '', endDate: '' };
    }

    const active = await fetchActiveSeasonDoc(teamId).catch((err) => {
      console.error('Failed to fetch active season for Head-to-head', err);
      return null;
    });

    const now = new Date();
    const seasonFromDate = normalized.find((s) => {
      const start = toDateValue(s.startDate);
      const end = toDateValue(s.endDate);
      if (!start || !end) return false;
      return start <= now && now <= end;
    });
    const storedSeasonId = getSeasonSelection(teamId);
    const storedMatch = findSeason(storedSeasonId);
    const targetYear = String(
      active?.team?.currentSeasonYear ||
      active?.year ||
      now.getFullYear()
    );

    const preferred =
      findSeason(targetYear) ||
      normalized.find((s) => s.status === 'active') ||
      seasonFromDate ||
      findSeason(active?.seasonId) ||
      findSeason(active?.year) ||
      storedMatch ||
      normalized[0] ||
      null;
    if (preferred) {
      return {
        seasonId: preferred.id,
        seasonConfigKey: preferred.year,
        startDate: preferred.startDate || '',
        endDate: preferred.endDate || '',
      };
    }

    const fallbackYear = String(
      active?.year ||
      active?.team?.currentSeasonYear ||
      new Date().getFullYear()
    );
    const fallbackSeasonId = String(active?.seasonId || fallbackYear);
    return { seasonId: fallbackSeasonId, seasonConfigKey: fallbackYear, startDate: '', endDate: '' };
  }, [teamId]);

  const fetchRowsFromCollection = useCallback(async (ref, teamKeyInput, options = {}) => {
    if (!ref) return [];
    const teamKeys = normalizeTeamKeys(teamKeyInput);
    const { allowUnfilteredFallback = false } = options;

    if (teamKeys.length) {
      const matchedGroups = await Promise.all(
        teamKeys.map(async (teamKey) => {
          const snap = await getDocs(query(ref, where('Team', '==', teamKey)));
          return snap.docs.map((d) => d.data());
        })
      );
      const matchedRows = mergeUniqueRows(...matchedGroups);
      if (matchedRows.length) return matchedRows;
    }

    const snap = await getDocs(ref);
    const rows = snap.docs.map((d) => d.data());
    if (allowUnfilteredFallback || !teamKeys.length) return rows;
    return rows.filter((row) => rowMatchesTeam(row, teamKeys));
  }, []);

  const fetchRowsForTeam = useCallback(async (statsCollection, teamKeyInput, options = {}) => {
    const teamKeys = normalizeTeamKeys(teamKeyInput);
    const loadFromBase = async (basePath) => {
      if (!basePath) return [];

      const modesRef = collection(db, basePath, 'modes');
      const modesRowsPromise = fetchRowsFromCollection(
        modesRef,
        teamKeys,
        {
          allowUnfilteredFallback:
            Boolean(options.allowUnfilteredFallback) && statsCollection === 'teamStats',
        }
      );

      if (statsCollection !== 'enemyStats') {
        return modesRowsPromise;
      }

      const matchGroupsRef = collection(db, basePath, 'matchGroups');
      const [modeRows, matchGroupRows] = await Promise.all([
        modesRowsPromise,
        fetchRowsFromCollection(matchGroupsRef, teamKeys),
      ]);
      return mergeUniqueRows(modeRows, matchGroupRows);
    };

    const seasonCandidates = [...new Set(
      [options.seasonConfigYear, options.seasonYear]
        .filter(Boolean)
        .map((value) => String(value))
    )];
    const legacyBasePath = `teams/${teamId}/database/${statsCollection}`;

    // Prefer year-based season docs, then season doc IDs, then legacy roots.
    for (const seasonKey of seasonCandidates) {
      const seasonBasePath = `teams/${teamId}/seasons/${seasonKey}/database/${statsCollection}`;
      const seasonRows = await loadFromBase(seasonBasePath);
      if (seasonRows.length) return seasonRows;
    }

    return loadFromBase(legacyBasePath);
  }, [db, teamId, fetchRowsFromCollection]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      if (!u) navigate('/landing'); else setUser(u);
    });
    return unsub;
  }, [auth, navigate]);

  useEffect(() => {
    if (!user) return;
    getDoc(doc(db, `users/${user.uid}`)).then(snap => {
      if (snap.exists()) setTeamId(snap.data().teamId);
    });
  }, [user, db]);

  useEffect(() => {
    if (!teamId) return;
    let alive = true;
    (async () => {
    const teamSnap = await getDoc(doc(db, `teams/${teamId}`));
    if (!alive) return;
    if (teamSnap.exists() && alive) {
      const t = teamSnap.data();
      const baseName = (t.name || '').trim();
      setBaseOwnerTeamLabel(baseName);
      setOwnerTeamLabel(baseName);
      setOwnerTeamName(baseName.toUpperCase());
    }

    const resolved = await resolveSeasonContext();
    if (!alive || !resolved) return;
    setSeasonYearA(resolved.seasonId);
    setSeasonConfigYearA(resolved.seasonConfigKey);
    setStartDateA(resolved.startDate || '');
    setEndDateA(resolved.endDate || '');
    setSeasonYearB(resolved.seasonId);
    setSeasonConfigYearB(resolved.seasonConfigKey);
    setStartDateB(resolved.startDate || '');
    setEndDateB(resolved.endDate || '');
  })().catch(console.error);

    const unsubscribe = subscribeSeasonSelection(teamId, (selectedSeasonId) => {
      resolveSeasonContext(selectedSeasonId)
        .then((resolved) => {
          if (!alive || !resolved) return;
          setSeasonYearA(resolved.seasonId);
          setSeasonConfigYearA(resolved.seasonConfigKey);
          setStartDateA(resolved.startDate || '');
          setEndDateA(resolved.endDate || '');
          setSeasonYearB(resolved.seasonId);
          setSeasonConfigYearB(resolved.seasonConfigKey);
          setStartDateB(resolved.startDate || '');
          setEndDateB(resolved.endDate || '');
        })
        .catch(console.error);
    });

    return () => {
      alive = false;
      unsubscribe();
    };
  }, [teamId, db, resolveSeasonContext]);

  useEffect(() => {
    if (!teamId) return;
    let alive = true;

    const resolveOwnerSeasonName = async () => {
      const seasonCandidates = [...new Set(
        [seasonConfigYearA, seasonYearA]
          .filter(Boolean)
          .map((value) => String(value))
      )];

      for (const seasonKey of seasonCandidates) {
        const seasonSnap = await getDoc(doc(db, `teams/${teamId}/seasons/${seasonKey}`));
        if (!seasonSnap.exists()) continue;
        const season = seasonSnap.data() || {};
        const seasonName = [
          season.displayName,
          season.teamName,
          season.externalTeamId,
          season.name
        ].find((value) => typeof value === 'string' && value.trim());

        if (seasonName) {
          const clean = seasonName.trim();
          if (!alive) return;
          setOwnerTeamLabel(clean);
          setOwnerTeamName(clean.toUpperCase());
          return;
        }
      }

      const fallback = (baseOwnerTeamLabel || config?.name || '').trim();
      if (!alive) return;
      setOwnerTeamLabel(fallback);
      setOwnerTeamName(fallback.toUpperCase());
    };

    resolveOwnerSeasonName().catch((err) => {
      console.error('Failed to resolve season team name for Head-to-head', err);
      const fallback = (baseOwnerTeamLabel || config?.name || '').trim();
      if (!alive) return;
      setOwnerTeamLabel(fallback);
      setOwnerTeamName(fallback.toUpperCase());
    });

    return () => {
      alive = false;
    };
  }, [teamId, db, seasonConfigYearA, seasonYearA, baseOwnerTeamLabel, config?.name]);

  useEffect(() => {
    if (!teamId) return;
    getDoc(doc(db, `teams/${teamId}/configurations/stats`)).then(snap => {
      if (!snap.exists()) return;
      const c = snap.data();
      const seasonConfig = getSeasonConfig(c, String(new Date().getFullYear()));
      setConfig(c);

      // Default A to "My Team", use config.region for lock
      setTeamA('__MY_TEAM__');
      setRegionA((c.region || '').toUpperCase());

      // Default B to first opponent from selected season
      setTeamB(seasonConfig.vsTeams?.[0]?.name || '');
      setRegionB((seasonConfig.vsTeams?.[0]?.region || c.region || '').toUpperCase());
    });
  }, [teamId, db]);

  useEffect(() => {
    if (!config) return;
    const seasonConfig = getSeasonConfig(config, seasonConfigYearA);
    const validMatchTypes = normalizeTeamKeys(
      getMatchTypeOptionsForRegions(seasonConfig.matchTypeCatalog || [], {
        category: matchCategoryA,
        regions: matchTypeRegionA,
      }).map(item => item.label)
    );
    setSelectedMatchTypesA((prev) =>
      prev.filter((type) => validMatchTypes.includes(normalizeText(type)))
    );
  }, [config, seasonConfigYearA, matchCategoryA, matchTypeRegionA]);

  useEffect(() => {
    if (!config) return;
    const seasonConfig = getSeasonConfig(config, seasonConfigYearB);
    const validMatchTypes = normalizeTeamKeys(
      getMatchTypeOptionsForRegions(seasonConfig.matchTypeCatalog || [], {
        category: matchCategoryB,
        regions: matchTypeRegionB,
      }).map(item => item.label)
    );
    setSelectedMatchTypesB((prev) =>
      prev.filter((type) => validMatchTypes.includes(normalizeText(type)))
    );
  }, [config, seasonConfigYearB, matchCategoryB, matchTypeRegionB]);

  // Fetch A: /teamStats/modes
useEffect(() => {
  (async () => {
    if (!db || !teamId || !teamA || !ownerTeamName || !config) return;

    const collA = myTeamA ? 'teamStats' : 'enemyStats';
    const myTeamKeys = normalizeTeamKeys([
      ownerTeamName,
      ownerTeamLabel,
      baseOwnerTeamLabel,
      config?.name,
      config?.teamName,
    ]);
    const teamKeyA = myTeamA
      ? myTeamKeys
      : (teamA || '').trim().toUpperCase();

    const fetchedRows = await fetchRowsForTeam(collA, teamKeyA, {
      allowUnfilteredFallback: myTeamA && collA === 'teamStats',
      seasonYear: seasonYearA,
      seasonConfigYear: seasonConfigYearA,
    });
    const rows = filterRowsForView(fetchedRows, {
      teamKeys: teamKeyA,
      region: myTeamA ? '' : regionA,
      sharedFilters: sharedFiltersA,
      allowUnfilteredFallback: myTeamA && collA === 'teamStats',
    });

    setSummaryA(buildModeSummary(rows));
    setMapDataA(buildMapStats(rows));
  })().catch(console.error);
}, [db, teamId, teamA, regionA, ownerTeamName, ownerTeamLabel, baseOwnerTeamLabel, config, myTeamA, seasonYearA, seasonConfigYearA, sharedFiltersA, fetchRowsForTeam]);

useEffect(() => {
  (async () => {
    if (!db || !teamId || !teamB || !ownerTeamName || !config) return;

    const collB = myTeamB ? 'teamStats' : 'enemyStats';
    const myTeamKeys = normalizeTeamKeys([
      ownerTeamName,
      ownerTeamLabel,
      baseOwnerTeamLabel,
      config?.name,
      config?.teamName,
    ]);
    const teamKeyB = myTeamB
      ? myTeamKeys
      : (teamB || '').trim().toUpperCase();

    const fetchedRows = await fetchRowsForTeam(collB, teamKeyB, {
      allowUnfilteredFallback: myTeamB && collB === 'teamStats',
      seasonYear: seasonYearB,
      seasonConfigYear: seasonConfigYearB,
    });
    const rows = filterRowsForView(fetchedRows, {
      teamKeys: teamKeyB,
      region: myTeamB ? '' : regionB,
      sharedFilters: sharedFiltersB,
      allowUnfilteredFallback: myTeamB && collB === 'teamStats',
    });

    setSummaryB(buildModeSummary(rows));
    setMapDataB(buildMapStats(rows));
  })().catch(console.error);
}, [db, teamId, teamB, regionB, ownerTeamName, ownerTeamLabel, baseOwnerTeamLabel, config, myTeamB, seasonYearB, seasonConfigYearB, sharedFiltersB, fetchRowsForTeam]);

  if (!config) return <div>Loading...</div>;

  const configA = { ...config, ...getSeasonConfig(config, seasonConfigYearA) };
  const configB = { ...config, ...getSeasonConfig(config, seasonConfigYearB) };
  const regionsListA = [...new Set([config.region, ...(configA.vsTeams || []).map(t => t.region)].filter(Boolean))];
  const regionsListB = [...new Set([config.region, ...(configB.vsTeams || []).map(t => t.region)].filter(Boolean))];
  const matchTypeRegionOptionsA = getMatchTypeRegions(configA.matchTypeCatalog || [], matchCategoryA);
  const matchTypeRegionOptionsB = getMatchTypeRegions(configB.matchTypeCatalog || [], matchCategoryB);
  const matchTypeOptionsA = getMatchTypeOptionsForRegions(configA.matchTypeCatalog || [], {
    category: matchCategoryA,
    regions: matchTypeRegionA,
  }).map(item => item.label);
  const matchTypeOptionsB = getMatchTypeOptionsForRegions(configB.matchTypeCatalog || [], {
    category: matchCategoryB,
    regions: matchTypeRegionB,
  }).map(item => item.label);
  const seasonOptions = availableSeasons.length
    ? availableSeasons
    : [{ id: seasonYearA, year: seasonConfigYearA || seasonYearA }];
  const selectedSeasonValueA = seasonOptions.find((season) => season.id === seasonYearA || season.year === seasonConfigYearA)?.id || seasonYearA;
  const selectedSeasonValueB = seasonOptions.find((season) => season.id === seasonYearB || season.year === seasonConfigYearB)?.id || seasonYearB;

  const handleSeasonChange = (side, value) => {
    const selected = seasonOptions.find((season) => season.id === value || season.year === value);
    resolveSeasonContext(selected?.id || value)
      .then((resolved) => {
        if (!resolved) return;
        if (side === 'A') {
          setSeasonYearA(resolved.seasonId);
          setSeasonConfigYearA(resolved.seasonConfigKey);
          setStartDateA(resolved.startDate || '');
          setEndDateA(resolved.endDate || '');
          return;
        }
        setSeasonYearB(resolved.seasonId);
        setSeasonConfigYearB(resolved.seasonConfigKey);
        setStartDateB(resolved.startDate || '');
        setEndDateB(resolved.endDate || '');
      })
      .catch(console.error);
  };

  const toggleMatchType = (side, type) => {
    const setSelectedMatchTypes = side === 'A' ? setSelectedMatchTypesA : setSelectedMatchTypesB;
    setSelectedMatchTypes((prev) => {
      const normalizedType = normalizeText(type);
      const exists = prev.some((item) => normalizeText(item) === normalizedType);
      if (exists) return prev.filter((item) => normalizeText(item) !== normalizedType);
      return [...prev, type];
    });
  };

  const toggleMatchTypeRegion = (side, region) => {
    const setMatchTypeRegion = side === 'A' ? setMatchTypeRegionA : setMatchTypeRegionB;
    const setSelectedMatchTypes = side === 'A' ? setSelectedMatchTypesA : setSelectedMatchTypesB;
    setMatchTypeRegion((prev) => {
      const normalizedRegion = normalizeText(region);
      const exists = normalizeTeamKeys(prev).includes(normalizedRegion);
      if (exists) return prev.filter((item) => normalizeText(item) !== normalizedRegion);
      return [...prev, region];
    });
    setSelectedMatchTypes([]);
  };

  const renderMatchRegionDropdown = (side, options, selectedRegions) => {
    const menuKey = `${side}-region`;
    const isOpen = matchTypeDropdownOpen === menuKey;
    const cleanOptions = [...new Set(options || [])].filter(Boolean);
    const label = selectedRegions.length === 0 || selectedRegions.length === cleanOptions.length
      ? 'All match regions'
      : selectedRegions.length === 1
        ? selectedRegions[0]
        : `${selectedRegions.length} match regions selected`;
    const setMatchTypeRegion = side === 'A' ? setMatchTypeRegionA : setMatchTypeRegionB;
    const setSelectedMatchTypes = side === 'A' ? setSelectedMatchTypesA : setSelectedMatchTypesB;

    return (
      <div className="h2h-match-type-dropdown">
        <button
          type="button"
          className="h2h-match-type-trigger"
          onClick={() => setMatchTypeDropdownOpen(isOpen ? null : menuKey)}
        >
          <span>{label}</span>
          <span aria-hidden="true">v</span>
        </button>
        {isOpen && (
          <div className="h2h-match-type-menu" role="listbox" aria-multiselectable="true">
            <div className="h2h-match-types-header">
              <span>Match Region</span>
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setMatchTypeRegion(cleanOptions);
                    setSelectedMatchTypes([]);
                  }}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMatchTypeRegion([]);
                    setSelectedMatchTypes([]);
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="h2h-checklist">
              {cleanOptions.length === 0 ? (
                <span className="h2h-empty-filter">All match regions</span>
              ) : (
                cleanOptions.map((region) => {
                  const checked = selectedRegions.some((item) => normalizeText(item) === normalizeText(region));
                  return (
                    <label
                      key={region}
                      className={`h2h-check-option ${checked ? 'is-selected' : ''}`}
                      role="option"
                      aria-selected={checked}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMatchTypeRegion(side, region)}
                      />
                      <span>{region}</span>
                      {checked && <span className="h2h-selected-mark" aria-hidden="true">{'\u2713'}</span>}
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMatchTypeDropdown = (side, options, selectedTypes) => {
    const menuKey = `${side}-type`;
    const isOpen = matchTypeDropdownOpen === menuKey;
    const label = selectedTypes.length === 0 || selectedTypes.length === options.length
      ? 'All match types'
      : selectedTypes.length === 1
        ? selectedTypes[0]
        : `${selectedTypes.length} match types selected`;
    const setSelectedMatchTypes = side === 'A' ? setSelectedMatchTypesA : setSelectedMatchTypesB;

    return (
      <div className="h2h-match-type-dropdown">
        <button
          type="button"
          className="h2h-match-type-trigger"
          onClick={() => setMatchTypeDropdownOpen(isOpen ? null : menuKey)}
        >
          <span>{label}</span>
          <span aria-hidden="true">v</span>
        </button>
        {isOpen && (
          <div className="h2h-match-type-menu" role="listbox" aria-multiselectable="true">
            <div className="h2h-match-types-header">
              <span>Match Type</span>
              <div>
                <button type="button" onClick={() => setSelectedMatchTypes(options)}>
                  Select all
                </button>
                <button type="button" onClick={() => setSelectedMatchTypes([])}>
                  Clear
                </button>
              </div>
            </div>
            <div className="h2h-checklist">
              {options.length === 0 ? (
                <span className="h2h-empty-filter">All match types</span>
              ) : (
                options.map((type) => {
                  const checked = selectedTypes.some((item) => normalizeText(item) === normalizeText(type));
                  return (
                    <label
                      key={type}
                      className={`h2h-check-option ${checked ? 'is-selected' : ''}`}
                      role="option"
                      aria-selected={checked}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMatchType(side, type)}
                      />
                      <span>{type}</span>
                      {checked && <span className="h2h-selected-mark" aria-hidden="true">{'\u2713'}</span>}
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSideFilters = ({
    side,
    selectedSeasonValue,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    matchCategory,
    setMatchCategory,
    matchTypeRegion,
    setMatchTypeRegion,
    matchTypeRegionOptions,
    matchTypeOptions,
    selectedMatchTypes,
  }) => (
    <div className="h2h-filter-bar h2h-filter-bar--column">
      <div className="h2h-filter-field">
        <label>Season</label>
        <select value={selectedSeasonValue} onChange={(e) => handleSeasonChange(side, e.target.value)}>
          {seasonOptions.map((season) => (
            <option key={season.id} value={season.id}>
              {season.year || season.id}
            </option>
          ))}
        </select>
      </div>

      <div className="h2h-filter-field">
        <label>Start Date</label>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
      </div>

      <div className="h2h-filter-field">
        <label>End Date</label>
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      <div className="h2h-filter-field">
        <label>Match Category</label>
        <select
          value={matchCategory}
          onChange={(e) => {
            setMatchCategory(e.target.value);
            setMatchTypeRegion([]);
            if (side === 'A') setSelectedMatchTypesA([]);
            if (side === 'B') setSelectedMatchTypesB([]);
          }}
        >
          <option value="">All categories</option>
          {MATCH_TYPE_CATEGORIES.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
      </div>

      <div className="h2h-filter-field">
        <label>Match Region</label>
        {renderMatchRegionDropdown(side, matchTypeRegionOptions, matchTypeRegion)}
      </div>

      <div className="h2h-filter-field">
        <label>Match Type</label>
        {renderMatchTypeDropdown(side, matchTypeOptions, selectedMatchTypes)}
      </div>
    </div>
  );

  return (
    <div className="head-to-head compact page-fit">
      <header className="h2h-page-header">
        <h2>Head-to-Head Comparison</h2>
        <p>Compare two teams across filters, map performance, and recent results.</p>
      </header>

      <div className="columns h2h-columns head-to-head-grid">

        <section className="column team-column">
          <div className="h2h-team-panel">
            <h3>Team 1</h3>
          <div className="filter-grid">
            <div className="h2h-filter-field">
              <label>Region</label>
              <select value={regionA} onChange={e => setRegionA(e.target.value)}>
                {regionsListA.map((r,i)=> <option key={`ra-${i}`} value={r}>{r}</option>)}
              </select>
            </div>
           <div className="h2h-filter-field">
  <label>Team</label>
  <select
  value={teamA}
  onChange={e => {
    const val = e.target.value;
    setTeamA(val);
    if (val === '__MY_TEAM__') setRegionA((config.region || '').toUpperCase());
  }}
>
  <option value="__MY_TEAM__">
    {ownerTeamLabel ? `Use ${ownerTeamLabel}` : (config?.name ? `Use ${config.name}` : 'Use my team')}
  </option>
  {(configA.vsTeams || [])
    .filter(t => (t.region || '').toUpperCase() === (regionA || '').toUpperCase())
    .map((t, i) => <option key={`tA-${i}`} value={t.name}>{t.name}</option>)}
</select>

</div>
          </div>
          {renderSideFilters({
            side: 'A',
            selectedSeasonValue: selectedSeasonValueA,
            startDate: startDateA,
            endDate: endDateA,
            setStartDate: setStartDateA,
            setEndDate: setEndDateA,
            matchCategory: matchCategoryA,
            setMatchCategory: setMatchCategoryA,
            matchTypeRegion: matchTypeRegionA,
            setMatchTypeRegion: setMatchTypeRegionA,
            matchTypeRegionOptions: matchTypeRegionOptionsA,
            matchTypeOptions: matchTypeOptionsA,
            selectedMatchTypes: selectedMatchTypesA,
          })}
          </div>
          <SummaryCards summary={summaryA} />
          <ModeTables data={mapDataA} />

<LastGamesViewer
  teamId={teamId}
  seasonId={seasonConfigYearA || seasonYearA}
  altSeasonId={seasonYearA}
  teamName={myTeamA ? ownerTeamName : teamA}
  isEnemy={!myTeamA}
  region={myTeamA ? '' : regionA}
  sharedFilters={sharedFiltersA}
/>
        </section>

              <section className="column team-column">
          <div className="h2h-team-panel">
            <h3>Team 2</h3>
          <div className="filter-grid">
            <div className="h2h-filter-field">
              <label>Region</label>
              <select value={regionB} onChange={e => setRegionB(e.target.value)}>
                {regionsListB.map((r,i)=> <option key={`rb-${i}`} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="h2h-filter-field">
              <label>Team</label>
<select
  value={teamB}
  onChange={e => {
    const val = e.target.value;
    setTeamB(val);
    if (val === '__MY_TEAM__') setRegionB((config.region || '').toUpperCase());
  }}
>
  <option value="__MY_TEAM__">
    {ownerTeamLabel ? `Use ${ownerTeamLabel}` : (config?.name ? `Use ${config.name}` : 'Use my team')}
  </option>
  {(configB.vsTeams || [])
    .filter(t => (t.region || '').toUpperCase() === (regionB || '').toUpperCase())
    .map((t,i)=> <option key={`tb-${i}`} value={t.name}>{t.name}</option>)}
</select>
            </div>
          </div>
          {renderSideFilters({
            side: 'B',
            selectedSeasonValue: selectedSeasonValueB,
            startDate: startDateB,
            endDate: endDateB,
            setStartDate: setStartDateB,
            setEndDate: setEndDateB,
            matchCategory: matchCategoryB,
            setMatchCategory: setMatchCategoryB,
            matchTypeRegion: matchTypeRegionB,
            setMatchTypeRegion: setMatchTypeRegionB,
            matchTypeRegionOptions: matchTypeRegionOptionsB,
            matchTypeOptions: matchTypeOptionsB,
            selectedMatchTypes: selectedMatchTypesB,
          })}
          </div>

          <SummaryCards summary={summaryB} />
          <ModeTables data={mapDataB} />

          {/* Last 15 Games for Team B */}
    
<LastGamesViewer
  teamId={teamId}
  seasonId={seasonConfigYearB || seasonYearB}
  altSeasonId={seasonYearB}
  teamName={myTeamB ? ownerTeamName : teamB}
  isEnemy={!myTeamB}
  region={myTeamB ? '' : regionB}
  sharedFilters={sharedFiltersB}
/>
        </section>

      </div> 
    </div>  
  );
}
